import { useEffect, useMemo, useState } from "react";
import { createMessage, MessageRole, MessageStatus } from "../model/message";
import { askQuestion } from "../../../api/chatApi";
import { finalizeOrder } from "../../../api/orderApi";
import {getOrCreateUserId, removeUserId} from "../../../utils/userId";
import { loadMessages, saveMessages, clearMessages, checkExpiration } from "../utils/storage";

const ONE_HOUR_MS = 60 * 60 * 1000;

export function useChat(restaurant, table) {
    const [userId, setUserId] = useState(() => getOrCreateUserId());
    const restaurantSlug = restaurant.slug;
    const [preparedOrder, setPreparedOrder] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const storageKey = useMemo(
        () => `chat:${restaurantSlug}:${table}:${userId}`,
        [restaurantSlug, table, userId]
    );
    const expiresKey = useMemo(() => `${storageKey}:expiresAt`, [storageKey]);

    const [messages, setMessages] = useState(() => loadMessages(storageKey, expiresKey));
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        saveMessages(storageKey, expiresKey, messages, ONE_HOUR_MS);
    }, [messages, storageKey, expiresKey]);

    useEffect(() => {
        const check = () => {
            if (checkExpiration(expiresKey)) {
                clearMessages(storageKey, expiresKey);
                setMessages([]);
            }
        };

        check();
        const id = setInterval(check, 30 * 1000);
        return () => clearInterval(id);
    }, [storageKey, expiresKey]);

    const resetChat = () => {
        clearMessages(storageKey, expiresKey);
        setMessages([]);
        setInput("");
        setLoading(false);
        removeUserId();

        setUserId(getOrCreateUserId());
    };

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        setMessages((prev) => [...prev, createMessage({ role: MessageRole.USER, text })]);
        setInput("");
        setLoading(true);

        try {
            const response = await askQuestion({
                restaurant: restaurantSlug,
                user: userId,
                table,
                question: text,
            });

            setMessages((prev) => [...prev, createMessage({ role: MessageRole.BOT, text: response.answer })]);

            if (response.preparedOrder) {
                setPreparedOrder(response.preparedOrder);
                setShowConfirmModal(true);
            }
        } catch {
            setMessages((prev) => [
                ...prev,
                createMessage({
                    role: MessageRole.BOT,
                    text: "Error: backend unavailable",
                    status: MessageStatus.ERROR,
                }),
            ]);
        } finally {
            setLoading(false);
        }
    };

    const addMessages = (question, answer) => {
        setMessages((prev) => [
            ...prev,
            createMessage({ role: MessageRole.USER, text: question }),
            createMessage({ role: MessageRole.BOT, text: answer }),
        ]);
    };

    const handleConfirmOrder = async () => {
        setMessages((prev) => [...prev, createMessage({ role: MessageRole.USER, text: "ПІДТВЕРДЖУЮ ЗАМОВЛЕННЯ" })]);
        setShowConfirmModal(false);
        setPreparedOrder(null);

        try {
            const response = await finalizeOrder({
                restaurant: restaurantSlug,
                user: userId,
                choice: "confirmOrder",
                table,
            });

            if (response?.answer) {
                setMessages((prev) => [...prev, createMessage({ role: MessageRole.BOT, text: response.answer })]);
            }
        } catch (error) {
            console.error("Error confirming order:", error);
        }
    };

    const handleDeclineOrder = async () => {
        setMessages((prev) => [...prev, createMessage({ role: MessageRole.USER, text: "НЕПРАВИЛЬНЕ ЗАМОВЛЕННЯ" })]);
        setShowConfirmModal(false);
        setPreparedOrder(null);

        try {
            const response = await finalizeOrder({
                restaurant: restaurantSlug,
                user: userId,
                choice: "declineOrder",
                table,
            });

            if (response?.answer) {
                setMessages((prev) => [...prev, createMessage({ role: MessageRole.BOT, text: response.answer })]);
            }
        } catch (error) {
            console.error("Error declining order:", error);
        }
    };

    return {
        userId,
        messages,
        input,
        setInput,
        loading,
        sendMessage,
        resetChat,
        addMessages,
        preparedOrder,
        showConfirmModal,
        handleConfirmOrder,
        handleDeclineOrder,
    };
}
